import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Award, UserCog, Building2, Infinity as InfIcon, AlertTriangle, Clock, RefreshCw, Plus, History, Ban, Gift } from "lucide-react";

/**
 * Painel Admin — Cortesias ativas + Histórico + Renovar direto na tabela.
 *
 * Abas:
 *  - "Ativas": lista atual, KPIs, renovar +N meses inline.
 *  - "Histórico": eventos de concessão/renovação/revogação (audit_logs).
 */
const TIER_STYLE = {
  ouro:   "bg-amber-100 text-amber-900 border-amber-300",
  prata:  "bg-slate-100 text-slate-800 border-slate-300",
  bronze: "bg-orange-100 text-orange-900 border-orange-300",
};
const ACTION_STYLE = {
  granted_courtesy:  { label: "Cortesia concedida", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: Gift },
  granted_lifetime:  { label: "Vitalício concedido", cls: "bg-amber-100 text-amber-800 border-amber-300",    Icon: InfIcon },
  renewed:           { label: "Renovada",             cls: "bg-sky-100 text-sky-800 border-sky-300",           Icon: Plus },
  revoked:           { label: "Revogada",             cls: "bg-red-100 text-red-800 border-red-300",           Icon: Ban },
};

const CourtesiesPanel = () => {
  const [tab, setTab] = useState("active"); // active | history
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [renewingId, setRenewingId] = useState(null);
  const [renewMonths, setRenewMonths] = useState(3);
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/courtesies"),
      api.get("/admin/courtesies/history?limit=200").catch(() => ({ data: [] })),
    ]).then(([a, h]) => {
      setData(a.data); setHistory(h.data || []);
    }).catch(() => toast.error("Erro ao carregar cortesias"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const renew = async (row) => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/courtesies/${row.kind}/${row.id}/renew`, { months: renewMonths });
      toast.success(`Renovada por +${renewMonths} mês(es) — nova expiração ${new Date(r.data.expires_at).toLocaleDateString("pt-BR")}`);
      setRenewingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao renovar");
    } finally { setBusy(false); }
  };

  const revoke = async (row) => {
    if (busy) return;
    const tag = row.is_lifetime ? "vitalícia" : "cortesia";
    if (!window.confirm(`Revogar ${tag} de "${row.label}"?\n\nO selo será removido, o(a) ${row.kind === "prof" ? "profissional" : "unidade"} receberá um e-mail avisando e a assinatura mensal ficará liberada.\n\nEsta ação é auditada.`)) return;
    setBusy(true);
    try {
      const endpoint = row.kind === "prof"
        ? `/admin/professionals/${row.id}/certification`
        : `/admin/clinics/${row.id}/certification`;
      await api.post(endpoint, {
        tier: "", months: 0, payment_status: "cancelled",
        is_lifetime: false, is_trial: false, note: null,
      });
      toast.success(`Cortesia revogada — e-mail enviado a ${row.label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao revogar");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-6 text-slate-500">Carregando cortesias…</div>;
  if (!data) return <div className="p-6 text-slate-500">Sem dados.</div>;

  const { counters, rows } = data;
  const filtered = rows.filter((r) => {
    if (filter === "expiring") return r.days_left !== null && r.days_left <= 30;
    if (filter === "lifetime") return r.is_lifetime;
    if (filter === "prof") return r.kind === "prof";
    if (filter === "clinic") return r.kind === "clinic";
    return true;
  });

  return (
    <div className="space-y-4" data-testid="courtesies-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-display font-bold text-[#0B2B62] text-lg flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600" /> Cortesias
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">Selos concedidos pela RAF sem cobrança. Revogar dispara e-mail e libera a assinatura mensal automaticamente.</p>
        </div>
        <button onClick={load} data-testid="courtesies-refresh" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {/* Sub-abas */}
      <div className="flex gap-1.5 border-b border-slate-200">
        <button onClick={() => setTab("active")} data-testid="courtesies-subtab-active"
          className={`text-xs px-3 py-2 border-b-2 -mb-px transition-colors ${tab === "active" ? "border-amber-600 text-amber-800 font-bold" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          <Award className="w-3.5 h-3.5 inline mr-1" /> Ativas ({counters.total})
        </button>
        <button onClick={() => setTab("history")} data-testid="courtesies-subtab-history"
          className={`text-xs px-3 py-2 border-b-2 -mb-px transition-colors ${tab === "history" ? "border-slate-600 text-slate-800 font-bold" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
          <History className="w-3.5 h-3.5 inline mr-1" /> Histórico ({history.length})
        </button>
      </div>

      {tab === "active" && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            <KPI label="Total ativas" value={counters.total} testid="kpi-total" />
            <KPI label="Vitalícias" value={counters.lifetime} icon={<InfIcon className="w-3.5 h-3.5" />} testid="kpi-lifetime" />
            <KPI label="Expiram ≤ 7 dias" value={counters.expiring_7d} tone={counters.expiring_7d > 0 ? "red" : "slate"} icon={<AlertTriangle className="w-3.5 h-3.5" />} testid="kpi-expiring-7d" />
            <KPI label="Expiram ≤ 30 dias" value={counters.expiring_30d} tone={counters.expiring_30d > 0 ? "amber" : "slate"} icon={<Clock className="w-3.5 h-3.5" />} testid="kpi-expiring-30d" />
            <KPI label="Profissionais" value={counters.by_kind.prof} icon={<UserCog className="w-3.5 h-3.5" />} testid="kpi-prof" />
            <KPI label="Unidades" value={counters.by_kind.clinic} icon={<Building2 className="w-3.5 h-3.5" />} testid="kpi-clinic" />
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {[
              { k: "all",      label: `Todas (${counters.total})` },
              { k: "expiring", label: `Expirando 30d (${counters.expiring_30d})` },
              { k: "lifetime", label: `Vitalícias (${counters.lifetime})` },
              { k: "prof",     label: `Profissionais (${counters.by_kind.prof})` },
              { k: "clinic",   label: `Unidades (${counters.by_kind.clinic})` },
            ].map((f) => (
              <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`courtesies-filter-${f.k}`}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${filter === f.k ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>{f.label}</button>
            ))}
            <div className="ml-auto flex items-center gap-1.5 text-xs text-slate-600">
              <span>Renovar por:</span>
              <select value={renewMonths} onChange={(e) => setRenewMonths(Number(e.target.value))}
                data-testid="renew-months-select"
                className="px-2 py-1 border border-slate-300 rounded text-xs">
                {[1, 2, 3, 6, 12, 24].map((m) => <option key={m} value={m}>+{m} {m === 1 ? "mês" : "meses"}</option>)}
              </select>
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg">Nenhuma cortesia neste filtro.</div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-lg">
              <table className="w-full text-xs" data-testid="courtesies-table">
                <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="text-left py-2 px-3">Tipo</th>
                    <th className="text-left py-2 px-3">Nome</th>
                    <th className="text-left py-2 px-3">Tier</th>
                    <th className="text-left py-2 px-3">Expira</th>
                    <th className="text-left py-2 px-3">Concedida em</th>
                    <th className="text-left py-2 px-3">Motivo</th>
                    <th className="text-right py-2 px-3">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((r) => (
                    <tr key={`${r.kind}-${r.id}`} data-testid={`courtesy-row-${r.id}`} className="hover:bg-slate-50">
                      <td className="py-2 px-3">
                        {r.kind === "prof" ? <span className="inline-flex items-center gap-1 text-indigo-700"><UserCog className="w-3.5 h-3.5" /> Prof</span>
                                            : <span className="inline-flex items-center gap-1 text-amber-700"><Building2 className="w-3.5 h-3.5" /> Unidade</span>}
                      </td>
                      <td className="py-2 px-3 font-medium text-[#0B2B62]">
                        {r.label}
                        {r.email && <div className="text-[10px] text-slate-500 font-normal">{r.email}</div>}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-block text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border ${TIER_STYLE[r.tier] || "bg-slate-100"}`}>{r.tier}</span>
                      </td>
                      <td className="py-2 px-3">
                        {r.is_lifetime ? (
                          <span className="inline-flex items-center gap-1 text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded font-bold text-[10px] uppercase"><InfIcon className="w-3 h-3" /> Vitalício</span>
                        ) : r.days_left !== null && r.days_left <= 7 ? (
                          <span className="text-red-700 font-bold">{r.days_left}d — {new Date(r.expires_at).toLocaleDateString("pt-BR")}</span>
                        ) : r.days_left !== null && r.days_left <= 30 ? (
                          <span className="text-amber-700 font-bold">{r.days_left}d — {new Date(r.expires_at).toLocaleDateString("pt-BR")}</span>
                        ) : (
                          <span className="text-slate-700">{r.days_left}d — {new Date(r.expires_at).toLocaleDateString("pt-BR")}</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-slate-600">{r.granted_at ? new Date(r.granted_at).toLocaleDateString("pt-BR") : "—"}</td>
                      <td className="py-2 px-3 text-slate-600 max-w-[200px] truncate" title={r.note || ""}>{r.note || "—"}</td>
                      <td className="py-2 px-3 text-right">
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          {!r.is_lifetime && (
                            <button onClick={() => renew(r)} disabled={busy}
                              data-testid={`renew-${r.id}`}
                              className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded border border-emerald-600 text-emerald-800 hover:bg-emerald-50 disabled:opacity-50">
                              <Plus className="w-3 h-3" /> +{renewMonths}m
                            </button>
                          )}
                          <button onClick={() => revoke(r)} disabled={busy}
                            data-testid={`revoke-${r.id}`}
                            className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded border border-red-500 text-red-700 hover:bg-red-50 disabled:opacity-50">
                            <Ban className="w-3 h-3" /> Revogar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "history" && (
        history.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-500 border border-dashed border-slate-300 rounded-lg">Nenhum evento de cortesia registrado ainda.</div>
        ) : (
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-xs" data-testid="courtesies-history-table">
              <thead className="bg-slate-50 text-slate-500 uppercase tracking-widest text-[10px]">
                <tr>
                  <th className="text-left py-2 px-3">Quando</th>
                  <th className="text-left py-2 px-3">Ação</th>
                  <th className="text-left py-2 px-3">Tipo</th>
                  <th className="text-left py-2 px-3">Nome</th>
                  <th className="text-left py-2 px-3">Tier</th>
                  <th className="text-left py-2 px-3">Por</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((h, i) => {
                  const st = ACTION_STYLE[h.action] || { label: h.action, cls: "bg-slate-100 text-slate-700 border-slate-300", Icon: Award };
                  const Ic = st.Icon;
                  return (
                    <tr key={i} data-testid={`history-row-${i}`}>
                      <td className="py-2 px-3 text-slate-600 whitespace-nowrap">{h.at ? new Date(h.at).toLocaleString("pt-BR") : "—"}</td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded border ${st.cls}`}>
                          <Ic className="w-3 h-3" /> {st.label}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        {h.kind === "prof" ? <span className="inline-flex items-center gap-1 text-indigo-700"><UserCog className="w-3.5 h-3.5" /> Prof</span>
                                            : <span className="inline-flex items-center gap-1 text-amber-700"><Building2 className="w-3.5 h-3.5" /> Unidade</span>}
                      </td>
                      <td className="py-2 px-3 font-medium text-[#0B2B62]">
                        {h.target_label}
                        {h.target_email && <div className="text-[10px] text-slate-500 font-normal">{h.target_email}</div>}
                      </td>
                      <td className="py-2 px-3">
                        {h.tier ? <span className={`inline-block text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded border ${TIER_STYLE[h.tier] || "bg-slate-100"}`}>{h.tier}</span> : "—"}
                      </td>
                      <td className="py-2 px-3 text-slate-600">{h.actor_name}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

const KPI = ({ label, value, tone = "slate", icon, testid }) => {
  const toneCls = {
    slate: "bg-slate-50 border-slate-200 text-slate-800",
    red:   "bg-red-50 border-red-200 text-red-800",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
  }[tone];
  return (
    <div data-testid={testid} className={`rounded-lg border p-3 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-1">
        {icon} {label}
      </div>
      <div className="text-2xl font-black font-display mt-1">{value}</div>
    </div>
  );
};

export default CourtesiesPanel;
